import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { History, LifeBuoy, Split, User, X } from 'lucide-react'
import type { CustomerQuickAddress, Order } from '@/types'
import {
  CounterGuestDetailsBody,
  hasGuestDetailsDraft,
} from '@/components/counter/CounterGuestDetailsSection'
import { CounterOrderHistoryBody } from '@/components/counter/CounterOrderHistorySection'
import { RecoveryCouponModal } from '@/components/counter/RecoveryCouponModal'
import { useModuleVisibility } from '@/hooks/useModuleVisibility'

export interface CounterRailToolbarProps {
  orderType: string
  existingOrder: Order | null
  continuingOrderId: string | null
  checkoutOpen: boolean

  // Guest CRM
  customerName: string
  setCustomerName: (v: string) => void
  customerEmail: string
  setCustomerEmail: (v: string) => void
  customerPhone: string
  setCustomerPhone: (v: string) => void
  guestBirthday: string
  setGuestBirthday: (v: string) => void
  deliveryAddress: string
  setDeliveryAddress: (v: string) => void
  /** Saved addresses of the picked customer — tap-to-switch chips on delivery. */
  savedAddresses?: CustomerQuickAddress[]
  setSavedAddresses?: (v: CustomerQuickAddress[]) => void
  onGuestUpdated: (order: Order) => void

  // Order history
  onSelectHistoryOrder: (order: Order) => void
  formatCurrency: (n: number) => string

  // Checkout
  onCloseCheckout: () => void

  // Split bill (F-FE-N19) — omit onOpenSplit to hide the button.
  onOpenSplit?: () => void
  /** True when a split session exists on the order being paid. */
  splitActive?: boolean
  /** Disable reason shown in the tooltip; undefined = enabled. */
  splitDisabledReason?: string
}

/**
 * Compact icon-toolbar that lives directly under the `TicketHeader` and hosts
 * the Customer and Order history popovers along with contextual actions
 * (recovery coupon, split bill, close checkout).
 *
 * The previous rail mounted these as always-expanded `<details>` blocks which
 * collectively consumed ~280–500 px of vertical space at the top of the
 * sidebar; collapsing them into icon-triggered popovers gives that space back
 * to the cart and the primary CTA.
 */
export function CounterRailToolbar({
  orderType,
  existingOrder,
  continuingOrderId,
  checkoutOpen,
  customerName,
  setCustomerName,
  customerEmail,
  setCustomerEmail,
  customerPhone,
  setCustomerPhone,
  guestBirthday,
  setGuestBirthday,
  deliveryAddress,
  setDeliveryAddress,
  savedAddresses,
  setSavedAddresses,
  onGuestUpdated,
  onSelectHistoryOrder,
  formatCurrency,
  onCloseCheckout,
  onOpenSplit,
  splitActive,
  splitDisabledReason,
}: CounterRailToolbarProps) {
  const [guestOpen, setGuestOpen] = useState(false)
  // True while the loyalty-card viewfinder is live inside the guest popover.
  // Chrome's camera permission bubble takes focus away from the document, which
  // Radix reads as a focus-outside and would use to close the popover — killing
  // the stream during the very prompt that authorises it.
  const [guestCameraActive, setGuestCameraActive] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [recoveryOpen, setRecoveryOpen] = useState(false)

  const isDelivery = orderType === 'delivery'
  // Delivery orders can't be placed without an address; flag the guest button
  // so the cashier knows where the missing piece lives.
  const deliveryAddressMissing = isDelivery && !deliveryAddress.trim()

  // Recovery coupon — available on any active (saved) order. The action is
  // PIN-gated server-side; here we just surface it and route to guest capture
  // when no customer is attached yet (the coupon must bind to a customer).
  const activeOrderId = existingOrder?.id ?? continuingOrderId ?? null
  // Recovery coupons are a Marketing Suite feature (ENABLED_MODULES).
  const marketingOn = useModuleVisibility().isEnabled('marketing')
  const recoveryCustomerCaptured = Boolean(existingOrder?.customer_id)

  const hasGuestDraft = hasGuestDetailsDraft({
    customerName,
    customerEmail,
    customerPhone,
    guestBirthday,
  })
  const closeAllPopovers = () => {
    setGuestOpen(false)
    setHistoryOpen(false)
  }

  const onSelectHistoryAndClose = (order: Order) => {
    closeAllPopovers()
    onSelectHistoryOrder(order)
  }

  return (
    <TooltipProvider delayDuration={250}>
      <div
        role="toolbar"
        aria-label="Ticket actions"
        className="flex items-center gap-1 overflow-x-auto border-b border-border/80 bg-card/90 px-3 py-2 sm:px-4"
      >
        {/* Guest CRM popover */}
        <Popover open={guestOpen} onOpenChange={setGuestOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant={guestOpen ? 'secondary' : 'ghost'}
                  size="icon"
                  className="relative h-9 w-9 shrink-0"
                  aria-label="Guest details"
                  aria-expanded={guestOpen}
                >
                  <User className="h-4 w-4" aria-hidden />
                  {deliveryAddressMissing ? (
                    <span
                      className="absolute right-1 top-1 h-2 w-2 rounded-full bg-destructive ring-2 ring-card"
                      aria-label="Delivery address required"
                    />
                  ) : hasGuestDraft ? (
                    <span
                      className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-primary"
                      aria-hidden
                    />
                  ) : null}
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">Guest details</TooltipContent>
          </Tooltip>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="w-[min(360px,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto p-3"
            // Only focus-outside is blocked, never pointer-outside: a cashier
            // must still be able to click away to dismiss the popover while the
            // viewfinder is running.
            onFocusOutside={(e) => {
              if (guestCameraActive) e.preventDefault()
            }}
          >
            <h3 className="mb-1.5 text-sm font-semibold tracking-tight text-foreground">
              Guest details
            </h3>
            <CounterGuestDetailsBody
              customerName={customerName}
              setCustomerName={setCustomerName}
              customerEmail={customerEmail}
              setCustomerEmail={setCustomerEmail}
              customerPhone={customerPhone}
              setCustomerPhone={setCustomerPhone}
              guestBirthday={guestBirthday}
              setGuestBirthday={setGuestBirthday}
              deliveryAddress={deliveryAddress}
              setDeliveryAddress={setDeliveryAddress}
              savedAddresses={savedAddresses}
              setSavedAddresses={setSavedAddresses}
              showDeliveryAddress={isDelivery}
              existingOrder={existingOrder}
              onCameraActiveChange={setGuestCameraActive}
              onGuestUpdated={onGuestUpdated}
              onClose={() => setGuestOpen(false)}
            />
          </PopoverContent>
        </Popover>

        {/* Order history popover */}
        <Popover open={historyOpen} onOpenChange={setHistoryOpen}>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant={historyOpen ? 'secondary' : 'ghost'}
                  size="icon"
                  className="h-9 w-9 shrink-0"
                  aria-label="Order history for current order type"
                  aria-expanded={historyOpen}
                >
                  <History className="h-4 w-4" aria-hidden />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent side="bottom">Order history (current order type)</TooltipContent>
          </Tooltip>
          <PopoverContent
            align="start"
            sideOffset={6}
            className="w-[min(360px,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto p-3"
          >
            <h3 className="mb-1.5 text-sm font-semibold tracking-tight text-foreground">
              Order history
            </h3>
            <CounterOrderHistoryBody
              orderType={orderType}
              formatCurrency={formatCurrency}
              onSelectOrder={onSelectHistoryAndClose}
            />
          </PopoverContent>
        </Popover>

        {/* Split bill — divide the check across guests (F-FE-N19) */}
        {onOpenSplit && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="shrink-0">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="relative h-9 w-9"
                  disabled={Boolean(splitDisabledReason)}
                  onClick={onOpenSplit}
                  aria-label="Split bill"
                >
                  <Split className="h-4 w-4" aria-hidden />
                  {splitActive && (
                    <span
                      className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-amber-500"
                      aria-label="Split in progress"
                    />
                  )}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              {splitDisabledReason ?? (splitActive ? 'Split in progress — resume' : 'Split bill')}
            </TooltipContent>
          </Tooltip>
        )}

        {/* Contextual actions — same row, no spacer, so the toolbar never wraps */}
        <div className="flex shrink-0 items-center gap-1">
          {marketingOn && activeOrderId && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-9 w-9 text-amber-600 hover:bg-amber-500/10 hover:text-amber-600 dark:text-amber-400"
                  onClick={() => setRecoveryOpen(true)}
                  aria-label="Issue recovery coupon"
                >
                  <LifeBuoy className="h-4 w-4" aria-hidden />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Recovery coupon (next visit)</TooltipContent>
            </Tooltip>
          )}

          {checkoutOpen && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-9 gap-1.5 px-2.5"
                  onClick={onCloseCheckout}
                  aria-label="Close checkout"
                >
                  <X className="h-3.5 w-3.5" aria-hidden />
                  <span className="text-xs font-medium">Close</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Close checkout</TooltipContent>
            </Tooltip>
          )}
        </div>

      </div>

      {marketingOn && recoveryOpen && activeOrderId && (
        <RecoveryCouponModal
          orderId={activeOrderId}
          customerCaptured={recoveryCustomerCaptured}
          existingOrder={existingOrder}
          customerName={customerName}
          setCustomerName={setCustomerName}
          customerEmail={customerEmail}
          setCustomerEmail={setCustomerEmail}
          customerPhone={customerPhone}
          setCustomerPhone={setCustomerPhone}
          guestBirthday={guestBirthday}
          setGuestBirthday={setGuestBirthday}
          onGuestUpdated={onGuestUpdated}
          onClose={() => setRecoveryOpen(false)}
        />
      )}
    </TooltipProvider>
  )
}
